import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import OtpInputBox from "./OtpInputBox";
import { useOtp } from "context/OtpContext";
import { OTP_LOGIN_ACTION, OTP_LENGTH } from "../../../../constants/authConstants";
const { verifyOtp, verifyCustomerOtp, resendOtp } = require("../../../authentication/_request");
import MDTypography from "components/MDTypography";
import { useAuth } from "context";
import MDButton from "components/MDButton";
import config from "config/config";
import { maskIdentifier } from "core/uitls/maskIdentifier";
import { getCustomerAPI, updateCustomerAPI } from "layouts/profile/_request";
import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";
import { AnalyticsEvents } from "core/constant";
import { useNavigate } from "react-router-dom";
import CampaignTargetingModal, { isCampaignTargetingError } from "components/custom/CampaignTargetingModal";
import TurnstileWidget from "components/TurnstileWidget";

const OtpInputForm = ({
  gtmCode,
  length,
  setLevel,
  setLoader,
  isGhl,
  ghlEmail,
  handleBack: closeModal,
  campaignCode,
  campaignDetails,
  storeId,
  brandId,
}) => {
  const configData = config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"];
  const navigate = useNavigate();
  const { otp, identifier, isOtpCompleted, isLoading, inputRefs, handleSendOtp, dispatch } =
    useOtp();
  const [targetingModal, setTargetingModal] = useState({ open: false, errorCode: null, tokens: null });

  const [resendCaptchaToken, setResendCaptchaToken] = useState(null);
  const resendTurnstileRef = useRef(null);
  const resetResendCaptcha = useCallback(() => {
    setResendCaptchaToken(null);
    resendTurnstileRef.current?.reset();
  }, []);

  const handleResend = () => {
    handleSendOtp(resendCaptchaToken, resetResendCaptcha);
  };

  const { saveAuthData, showSnackbarWithMessage, logout } = useAuth();
  const { trackCustomEvent } = useAnalyticsEvent();

  const handleBack = (e) => {
    e.preventDefault();

    dispatch({ type: OTP_LOGIN_ACTION.BACK });
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    if (isNaN(value) || value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

    if (index !== length - 1 && value) {
      const next = inputRefs.current[index + 1];
      if (next) next.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

      const prev = inputRefs.current[index - 1];
      if (prev) prev.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Filter out non-numeric characters
    const cleanedData = pastedData.replace(/\D/g, "");

    // Limit to the remaining available slots
    const remainingLength = length - index;
    const truncatedData = cleanedData.slice(0, remainingLength);

    if (!truncatedData) return;

    // Update OTP state with pasted digits
    const newOtp = [...otp];

    for (let i = 0; i < truncatedData.length; i++) {
      if (index + i < length) {
        newOtp[index + i] = truncatedData[i];
      }
    }

    dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

    // Focus on the next empty input or the last input
    const nextIndex = Math.min(index + truncatedData.length, length - 1);
    const nextInput = inputRefs.current[nextIndex];
    if (nextInput) nextInput.focus();
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });

    let otpValue = otp.join("");

    if (otpValue.length < OTP_LENGTH || isNaN(parseInt(otpValue)) || !identifier?.trim()) {
      dispatch({ type: OTP_LOGIN_ACTION.MALFORMED_REQUEST });
      return;
    }

    const {
      data: { success, message, data },
    } = await verifyOtp({ identifier, otp: otpValue });

    if (success) {
      const verifiedCustomer = await saveAuthData(data.tokens);
      if (data.isNewEmail) {
        setLevel(1); //level 1 is for profile details page
      } else {
        const {
          data: { data: customerDetail },
        } = await getCustomerAPI();
        if (
          !customerDetail?.customer?.firstName &&
          (!customerDetail?.customer?.lastName || !customerDetail?.customer?.phoneNumber)
        ) {
          setLevel(1); //level 1 is for profile details page
        } else {
          // Enroll existing customer in campaign/store/brand if they signed in from a campaign landing page
          if (campaignCode || storeId || brandId) {
            try {
              const {
                data: { success: enrollSuccess, message: enrollMessage, errorCode: enrollErrorCode },
              } = await updateCustomerAPI({
                ...(storeId
                  ? {
                      restaurantStoreId: storeId,
                      ...(!campaignDetails && { origin: "LANDING" }),
                    }
                  : brandId
                  ? {
                      brandId,
                      ...(!campaignDetails && { origin: "BRAND_LANDING" }),
                    }
                  : {}),
                campaignCode: campaignDetails ? campaignCode : null,
              });

              if (!enrollSuccess) {
                if (isCampaignTargetingError(enrollErrorCode)) {
                  setTargetingModal({ open: true, errorCode: enrollErrorCode, tokens: data.tokens });
                  dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: new Array(length).fill("") });
                  dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
                  return;
                }
                showSnackbarWithMessage({
                  type: "error",
                  message: enrollMessage,
                });
                dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
                return;
              }
            } catch (err) {
              console.error("Campaign enrollment failed for existing customer", err);
              showSnackbarWithMessage({
                type: "error",
                message: "Something went wrong. Please try again.",
              });
              dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
              return;
            }
          }

          trackCustomEvent(
            AnalyticsEvents.LOGIN,
            {
              method: "otp",
              auth_origin: "landing_portal",
              user_id: verifiedCustomer.id,
              user_role: verifiedCustomer.role,
              user_type: verifiedCustomer.userType,
            },
            false
          );

          showSnackbarWithMessage({
            type: "success",
            message,
          });
          let cleanGtmCode = "";
          if (gtmCode) {
            cleanGtmCode = gtmCode.replace("GTM-", "");
          }
          const url = `${
            configData.CUSTOMER_LINKS.SIGN_IN
          }?landing_page_tokens=${encodeURIComponent(
            JSON.stringify(data.tokens)
          )}&s=${cleanGtmCode}`;
          if (url) {
            window.location.href = url;
          }
          logout();
          setLevel(0); //level 1 is for profile details page
        }
      }
    } else {
      showSnackbarWithMessage({
        type: "error",
        message,
      });
    }

    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
  };

  // Verify users who registered from GHL form
  const handleVerify = async (event) => {
    event.preventDefault();
    setLoader(true);
    let otpValue = otp.join("");

    try {
      const {
        data: { success, message, data },
      } = await verifyCustomerOtp({ code: +otpValue });
      if (success) {
        setLoader(false);

        await saveAuthData(data.tokens);

        showSnackbarWithMessage({
          type: "success",
          message: "Success! Welcome to Catering Rewards!",
        });
        const cleanGtmCode = gtmCode && gtmCode.replace("GTM-", "");
        let url = `${configData.CUSTOMER_LINKS.SIGN_IN}?landing_page_tokens=${encodeURIComponent(
          JSON.stringify(data.tokens)
        )}`;

        if (cleanGtmCode) {
          url += `&s=${cleanGtmCode}`;
        }

        if (url) {
          window.location.href = url;
        }
        logout();
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
        setLoader(false);
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
      setLoader(false);
    }
  };

  const handleReSendOTP = async (index) => {
    setLoader(true);
    try {
      const {
        data: { success, message, data },
      } = await resendOtp(false, ghlEmail);
      if (success) {
        showSnackbarWithMessage({
          type: "success",
          message,
        });
        setLoader(false);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
        setLoader(false);
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
      setLoader(false);
    }
  };

  const handleTargetingContinue = async () => {
    // Existing user is already linked — just redirect to customer portal
    let cleanGtmCode = "";
    if (gtmCode) {
      cleanGtmCode = gtmCode.replace("GTM-", "");
    }
    const url = `${configData.CUSTOMER_LINKS.SIGN_IN}?landing_page_tokens=${encodeURIComponent(
      JSON.stringify(targetingModal.tokens)
    )}&s=${cleanGtmCode}`;
    logout();
    window.location.href = url;
  };

  return (
    <>
      <CampaignTargetingModal
        open={targetingModal.open}
        onClose={() => setTargetingModal({ open: false, errorCode: null, tokens: null })}
        errorCode={targetingModal.errorCode}
        onContinue={handleTargetingContinue}
      />
      {/* Email Details  */}
      <Box sx={{ mt: "-34px" }} display="flex" flexDirection="column" alignItems="center">
        <p
          style={{
            fontSize: 16,
            color: "#475467",
            lineHeight: "20px",
            textAlign: "center",
          }}
        >
          We&apos;ve sent a 6 digit code to{" "}
          <span style={{ fontWeight: 600 }}>{ghlEmail || maskIdentifier(identifier)}</span>
        </p>

        <Box
          mt={"30px"}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="start"
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {otp.map((value, index) => (
              <React.Fragment key={index}>
                <OtpInputBox
                  value={value}
                  onPaste={(e) => handlePaste(e, index)}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                />
              </React.Fragment>
            ))}
          </div>
          {/* Resend OTP Code  */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            style={{ marginTop: "6px" }}
          >
            {!isGhl && (
              <TurnstileWidget
                ref={resendTurnstileRef}
                siteKey={configData.TURNSTILE_SITE_KEY}
                onToken={setResendCaptchaToken}
                onExpire={() => setResendCaptchaToken(null)}
                onError={() => setResendCaptchaToken(null)}
              />
            )}
            <MDTypography
              color={"textSecondary"}
              sx={{ fontSize: "14px", ml: "2px" }}
              fontWeight="normal"
            >
              Didn’t get a code?
              <span
                style={{
                  textDecoration: "underline",
                  cursor: !isGhl && !resendCaptchaToken ? "default" : "pointer",
                  marginLeft: "3px",
                  opacity: !isGhl && !resendCaptchaToken ? 0.5 : 1,
                }}
                onClick={isGhl ? handleReSendOTP : resendCaptchaToken ? handleResend : undefined}
              >
                Click to resend.
              </span>
            </MDTypography>
          </Box>
        </Box>
      </Box>

      {/* BACK AND NEXT BTN  */}
      <Box display="flex" gap={2} alignItems="center" style={{ marginTop: "30px" }}>
        <MDButton
          fullWidth
          onClick={closeModal ? closeModal : handleBack}
          variant="outlined"
          color="black"
          sx={{
            borderRadius: "8px",
            padding: "10px",
            border: "1px solid #D0D5DD",
            textDecoration: "lowercase",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          }}
        >
          Back
        </MDButton>

        <MDButton
          fullWidth
          variant="contained"
          color="success"
          disabled={isLoading || !isOtpCompleted}
          onClick={isGhl ? handleVerify : handleVerifyOtp}
        >
          Next
        </MDButton>
      </Box>
    </>
  );
};

OtpInputForm.propTypes = {
  length: PropTypes.number.isRequired,
  setLevel: PropTypes.any,
  handleBack: PropTypes.func,
  gtmCode: PropTypes.string,
  isGhl: PropTypes.bool,
  ghlEmail: PropTypes.string,
  setLoader: PropTypes.func,
  campaignCode: PropTypes.string,
  campaignDetails: PropTypes.object,
  storeId: PropTypes.string,
  brandId: PropTypes.string,
};

export default OtpInputForm;
